// PRD-1359 — the review-required warnings shown in ResAI and both Automation
// wizards.
//
// Exclusion is DOCUMENT-level, not dataset-level: only READY documents reach the
// retrieval set, and a dataset holding parked documents stays selectable minus
// those documents. Confirmed with backend — ready fields are queryable, parked
// ones are not. So every string here WARNS that data may be missing; none of
// them blocks, and nothing in this module should ever be wired to a `disabled`.
//
// That also makes these strings load-bearing rather than decorative: they are
// the only signal that an answer was computed on partial data.
//
// The predicate is `isAwaitingReviewOnly`, never a badge test — see
// utils/datasetReviewState.js for why a badge test misses a dataset that is both
// parked and failed (badge precedence is processing > failed > review).

import { isAwaitingReviewOnly } from './datasetReviewState';

/**
 * The review-required label. Matches the dataset badge wording so the two read
 * as one state. Sentence case, per design's platform-wide rule for chips and
 * buttons — the only exception is a named area of the platform, which is why
 * REVIEW_LINK_TEXT below keeps its capitals.
 *
 * Rendered as a CHIP in the Automation wizard's dataset list and as a plain LINK
 * in ResAI's dataset rail; the two frames differ, so the styling is the call
 * site's and only the wording is shared.
 */
export const REVIEW_REQUIRED_LABEL = 'Review required';

/** Link text. Identical in both flows, per the feedback doc. "Data Sources" is
 *  a named area of the platform, so it stays capitalised. */
export const REVIEW_LINK_TEXT = 'Open in Data Sources';

/**
 * The Data Sources deep link behind REVIEW_LINK_TEXT.
 *
 * With no dataset, the Datasets page filtered to review-required (what the
 * Automations copy promises). With one, that same filtered page plus the
 * dataset opened, which is as close to "Field Review for that dataset" as we
 * can get without a review permissions capability to gate it on — that question
 * is still open on PRD-1359.
 */
export function reviewDataSourcesHref(datasetId) {
  return datasetId
    ? `/data-sources?review=required&dataset=${encodeURIComponent(datasetId)}`
    : '/data-sources?review=required';
}

/** Parked-document count for one dataset, 0 when nothing is parked. */
export function reviewRequiredCount(dataset) {
  return isAwaitingReviewOnly(dataset) ? (dataset.file_counts?.awaiting_review ?? 0) : 0;
}

/** The subset of `datasets` holding parked documents with nothing in flight. */
export function reviewRequiredDatasets(datasets) {
  return (datasets || []).filter(isAwaitingReviewOnly);
}

// "1 document ... requires" vs "2 documents ... require".
function documentsPhrase(n) {
  return n === 1 ? '1 document' : `${n} documents`;
}

/**
 * "1 document requires review" vs "2 documents require review".
 *
 * Exported because the same agreement is needed outside the notices — the
 * dataset details count line and the Edit drawer's chip both carried a fixed
 * plural, so "1 require review" reached the screen in two more places after the
 * notices themselves were fixed. Third and fourth copies of a rule are how that
 * happens; there is now one.
 */
export function requireVerb(n) {
  return n === 1 ? 'requires' : 'require';
}

/**
 * The subject clause both banners open with, plus the count that built it.
 *
 * The feedback doc's copy names a single dataset ("2 documents in [dataset name]
 * require review"), which is the case the designs show. ResAI and the wizards
 * both allow a multi-select, so a second form is needed — it counts datasets
 * rather than naming them, because naming four datasets inline produces a banner
 * nobody reads.
 *
 * `total` is returned rather than kept private because the sentence that FOLLOWS
 * has to agree with it. Both notices used a fixed plural pronoun, so one parked
 * document read "1 document in Claims requires review. Unless they're reviewed
 * ... their data won't be included" — the subject agreed and the rest did not.
 *
 * @returns {{clause: string, total: number}|null} null when nothing is parked.
 */
function subject(datasets) {
  const parked = reviewRequiredDatasets(datasets);
  if (parked.length === 0) return null;

  const total = parked.reduce((sum, d) => sum + reviewRequiredCount(d), 0);
  if (total === 0) return null;

  const clause = parked.length === 1
    ? `${documentsPhrase(total)} in ${parked[0].name} ${requireVerb(total)} review`
    : `${documentsPhrase(total)} across ${parked.length} datasets ${requireVerb(total)} review`;
  return { clause, total };
}

/**
 * ResAI: shown above Sources used when a query ran against parked documents.
 * Past tense on purpose — the answer is already on screen, so the warning is
 * about what that answer was computed from.
 *
 * @returns {string|null} null when nothing is parked, i.e. render no banner.
 */
export function resaiReviewNotice(datasets) {
  const s = subject(datasets);
  if (!s) return null;
  // Agrees with the subject: one document is an "it", not a "they".
  const pronoun = s.total === 1 ? 'Its' : 'Their';
  return `${s.clause}. ${pronoun} data may be missing.`;
}

/**
 * Automations: shown on step 2 under the dataset select and again on step 4.
 * Future tense — the automation has not run yet, so the reviewer still has time
 * to clear the queue and change the outcome.
 *
 * @returns {string|null} null when nothing is parked.
 */
export function automationReviewNotice(datasets) {
  const s = subject(datasets);
  if (!s) return null;
  // Two agreements in one sentence: the verb ("it's" / "they're") and the
  // possessive ("its" / "their").
  const one = s.total === 1;
  const subjectPronoun = one ? "it's" : "they're";
  const possessive = one ? 'its' : 'their';
  return `${s.clause}. Unless ${subjectPronoun} reviewed before this automation runs, `
    + `${possessive} data won't be included.`;
}

// Named rather than an anonymous object literal: `import/no-anonymous-default-export`
// fails the CI build, and a name is what shows up in a stack trace.
const reviewRequiredNotice = {
  REVIEW_REQUIRED_LABEL,
  REVIEW_LINK_TEXT,
  reviewDataSourcesHref,
  reviewRequiredCount,
  reviewRequiredDatasets,
  resaiReviewNotice,
  automationReviewNotice,
};

export default reviewRequiredNotice;
